import React from 'react';
import { AudioFile, Transcript } from '../../types';
import { formatDateTimestamp, formatDuration } from '../../utils/formatters';
import StatCard from '../ui/StatCard';
import TranscriptSummary from './TranscriptSummary';
import TranscriptNotes from './TranscriptNotes';

// Shared styles constants
const CONTAINER_STYLES = {
  showMode:
    'mb-4 p-4 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800',
};

interface TranscriptMetaProps {
  transcript: Transcript | null;
  audioFile: AudioFile;
  audioFileId: string;
  onTranscriptUpdate: (updatedTranscript: Transcript) => void;
}

const TranscriptMeta: React.FC<TranscriptMetaProps> = ({
  transcript,
  audioFile,
  audioFileId,
  onTranscriptUpdate,
}) => {
  // Only show transcript details and notes if file is ready and has transcript
  const showTranscriptSections =
    audioFile.status === 'ready' && audioFile.hasTranscript && transcript;

  return (
    <div>
      {/* Only show transcript sections when file is ready and has transcript */}
      {showTranscriptSections ? (
        <>
          {/* AI-generated Summary */}
          <TranscriptSummary
            transcript={transcript}
            audioFileId={audioFileId}
            onTranscriptUpdate={onTranscriptUpdate}
          />

          {/* Transcript Details - Non-editable */}
          <div className={CONTAINER_STYLES.showMode}>
            <div className="flex items-center gap-2 mb-3">
              <span className="font-medium text-gray-900 dark:text-gray-100">
                Transcript Details
              </span>
            </div>
            <div className="space-y-2">
              <StatCard
                variant="row"
                label="Duration"
                value={formatDuration(transcript!.duration, true)}
              />
              <StatCard
                variant="row"
                label="Accuracy"
                value={`${transcript!.accuracyPercentage}%`}
              />
              <StatCard
                variant="row"
                label="Speakers"
                value={transcript!.speakerCount}
              />
              <StatCard
                variant="row"
                label="Word count"
                value={transcript!.wordCount.toLocaleString()}
              />
              {process.env.REACT_APP_TRANSCRIBING_SERVICE_SELECTION ===
                'true' && (
                <StatCard
                  variant="row"
                  label="Service"
                  value={transcript!.transcriptionService}
                />
              )}
              {process.env.REACT_APP_DEBUG_TRANSCRIPT_META === 'true' && (
                <>
                  <StatCard
                    variant="row"
                    label="Started"
                    value={formatDateTimestamp(transcript!.startedAt)}
                  />
                  <StatCard
                    variant="row"
                    label="Completed"
                    value={formatDateTimestamp(transcript!.completedAt)}
                  />
                </>
              )}
            </div>
          </div>

          {/* Notes - Editable */}
          <TranscriptNotes
            transcript={transcript}
            audioFileId={audioFileId}
            onTranscriptUpdate={onTranscriptUpdate}
          />
        </>
      ) : (
        <div className="text-sm text-gray-500 dark:text-gray-400 p-4 border border-gray-200 dark:border-gray-600 rounded-lg">
          {audioFile.status === 'processing' ? (
            <p>
              Transcript is being processed. Details will appear when
              transcription is complete.
            </p>
          ) : audioFile.status === 'failed' ? (
            <p>Transcription failed. Please try uploading the file again.</p>
          ) : !audioFile.hasTranscript ? (
            <p>No transcript available for this file.</p>
          ) : (
            <p>Loading transcript details...</p>
          )}
        </div>
      )}
    </div>
  );
};

export default TranscriptMeta;
